<script lang="ts" setup>
import { useLanguage } from '#composables/useLanguage';
import {
  customDateToDate,
  dateToCustomDate,
  type CarrefourReferentialIntervalType,
  type DatePeriodIntervalType,
  type DateSelectionOption,
  type ProductTaxonomyType,
  type ReferentialIntervalsType
} from '@carrefour-gcs/shared';
import { computed, watch } from 'vue';
import CustomIntervalSelector from './CustomIntervalSelector.vue';
import DateOptionsSelector from './dateOptionsSelector/DateOptionsSelector.vue';
import type { SelectedIntervalType } from './periodSelector.model';
import {
  getCarrefourPeriodInterval,
  getCurrentDateReferentialInterval,
  getDateCumulativeOptions
} from './periodSelectorUtils';

const props = defineProps<{
  currentTaxonomy: ProductTaxonomyType;
  referentialIntervals: ReferentialIntervalsType;
  carrefourReferentialIntervals: keyof CarrefourReferentialIntervalType;
  selectedPeriod: DatePeriodIntervalType;
  availableInterval?: { start: Date; end: Date };
}>();

const emit = defineEmits<{
  'update-period': [period: DatePeriodIntervalType];
}>();

const { t } = useLanguage();

const referentialInterval = computed(() =>
  getCurrentDateReferentialInterval({
    currentTaxonomy: props.currentTaxonomy,
    referentialIntervals: props.referentialIntervals,
    selectedPeriodType: props.selectedPeriod.period,
    carrefourReferentialIntervals: props.carrefourReferentialIntervals
  })
);

const defaultAvailableInterval = computed(() => ({
  start: customDateToDate(referentialInterval.value.start),
  end: customDateToDate(referentialInterval.value.end)
}));

const effectiveAvailableInterval = computed(() => props.availableInterval ?? defaultAvailableInterval.value);

const cumulativeOptions = computed(() =>
  getDateCumulativeOptions(props.selectedPeriod.period, customDateToDate(referentialInterval.value.end), t).filter(
    // For an unknown reason, 'last-period is disabled on a daily period'
    (option) => !(props.selectedPeriod.period === 'daily' && option.value === 'last_period')
  )
);

const datePeriodInterval = computed((): SelectedIntervalType => {
  const fromDate = customDateToDate(props.selectedPeriod.periodInterval.start);
  const toDate = customDateToDate(props.selectedPeriod.periodInterval.end);
  const minStart = effectiveAvailableInterval.value.start;

  const from = fromDate.getTime() < minStart.getTime() ? minStart : fromDate;
  const to = toDate.getTime() < from.getTime() ? from : toDate;

  return {
    from,
    to
  };
});

const isDateRangeActive = computed(() => props.selectedPeriod.option === 'dateRange');

// Reset selected cumulativeOption on period change
watch(
  () => props.selectedPeriod.period,
  function removeLastPeriodIfNielsenOrDailyIsPicked() {
    if (props.selectedPeriod.period === 'daily' && props.selectedPeriod.option === 'last_period') {
      onCheck('dateRange');
    }
  }
);

const onCheck = (value: DateSelectionOption) => {
  const updatedPeriod = getCarrefourPeriodInterval({
    selectedPeriod: props.selectedPeriod.period,
    currentDateReferentialInterval: referentialInterval.value,
    selectedInterval: props.selectedPeriod.periodInterval,
    selectedCalendarOption: value
  });
  emit('update-period', {
    ...props.selectedPeriod,
    option: value,
    periodInterval: updatedPeriod
  });
};

const updateSelectedInterval = (newInterval: SelectedIntervalType) => {
  emit('update-period', {
    ...props.selectedPeriod,
    periodInterval: {
      start: dateToCustomDate(newInterval.from),
      end: dateToCustomDate(newInterval.to)
    }
  });
};
</script>

<template>
  <div>
    <div class="flex flex-col w-full mt-4">
      <CustomIntervalSelector
        :is-active="isDateRangeActive"
        :name="'cumulative-options'"
        :label="t('timeSelectorPopin.customInterval')"
        :period="selectedPeriod.period"
        :available-interval="effectiveAvailableInterval"
        :selected-interval="datePeriodInterval"
        :hide-component-when-inactive="false"
        test-id="carrefour-date-range-radio"
        @select="onCheck('dateRange')"
        @update:interval="updateSelectedInterval($event)"
      />

      <DateOptionsSelector
        :selected-value="selectedPeriod.option"
        name="cumulative-options"
        :options="cumulativeOptions"
        @check="onCheck($event)"
      />
    </div>
  </div>
</template>
